import React from 'react';
import {
    Mail,
    Facebook,
    Instagram,
    Smartphone,
    FileText,
    Shield,
    Cookie,
    HelpCircle,
    Info,
    AlertCircle
} from 'lucide-react';

// ===== CUSTOM SVG BADGES =====

// Apple App Store Badge
const AppStoreBadge = () => (
    <a href="#" className="group block">
        <svg viewBox="0 0 160 50" className="w-full h-auto transition-transform group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="160" height="50" rx="8" fill="#000000" />
            <text x="80" y="18" textAnchor="middle" fill="#86868b" fontSize="9" fontFamily="system-ui" fontWeight="400">
                Download on the
            </text>
            <text x="80" y="36" textAnchor="middle" fill="#ffffff" fontSize="17" fontFamily="system-ui" fontWeight="600">
                App Store
            </text>
        </svg>
    </a>
);

// Google Play Store Badge
const GooglePlayBadge = () => (
    <a href="#" className="group block">
        <svg viewBox="0 0 160 50" className="w-full h-auto transition-transform group-hover:scale-105" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="160" height="50" rx="8" fill="#000000" />
            <text x="80" y="18" textAnchor="middle" fill="#86868b" fontSize="9" fontFamily="system-ui" fontWeight="400">
                Get it on
            </text>
            <text x="80" y="36" textAnchor="middle" fill="#ffffff" fontSize="17" fontFamily="system-ui" fontWeight="600">
                Google Play
            </text>
        </svg>
    </a>
);

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const aboutLinks = [
        { label: 'Informații despre operator', icon: <Info size={14} />, href: '/operator' },
        { label: 'Disclaimer', icon: <AlertCircle size={14} />, href: '/disclaimer' },
        { label: 'Politica de confidențialitate', icon: <Shield size={14} />, href: '/confidentialitate' },
        { label: 'Politica de cookies', icon: <Cookie size={14} />, href: '/cookies' },
        { label: 'Termeni și condiții', icon: <FileText size={14} />, href: '/termeni' },
        { label: 'FAQs', icon: <HelpCircle size={14} />, href: '/faq' },
    ];

    const contactLinks = [
        { label: 'support@affinityclub.agency', icon: <Mail size={14} />, href: 'mailto:support@affinityclub.agency' },
        { label: 'admin@affinityclub.agency', icon: <Mail size={14} />, href: 'mailto:admin@affinityclub.agency' },
        
    ];

    return (
        <footer className="bg-gradient-to-b from-stone-900 via-stone-900 to-stone-950 text-white relative overflow-hidden">
            {/* Decorative top border */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />

            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-[0.02]"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '40px 40px'
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 py-16">
                {/* 3 Column Layout */}
                <div className="grid md:grid-cols-3 gap-12 md:gap-8">

                    {/* ===== LEFT COLUMN - DESPRE NOI ===== */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center">
                                <Info className="text-white" size={16} />
                            </div>
                            <h3 className="text-lg font-bold" style={{ fontFamily: "'Georgia', serif" }}>
                                Despre Noi
                            </h3>
                        </div>

                        <nav className="space-y-3">
                            {aboutLinks.map((link, i) => (
                                <a
                                    key={i}
                                    href={link.href}
                                    className="group flex items-center gap-3 text-stone-400 hover:text-white transition-all duration-300 text-sm"
                                    style={{ fontFamily: "system-ui, sans-serif" }}
                                >
                                    <span className="text-stone-500 group-hover:text-rose-400 transition-colors">
                                        {link.icon}
                                    </span>
                                    <span className="group-hover:translate-x-1 transition-transform">
                                        {link.label}
                                    </span>
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* ===== CENTER COLUMN - MOBILE APPS ===== */}
                    <div className="space-y-6 text-center">
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center">
                                <Smartphone className="text-white" size={16} />
                            </div>
                            <h3 className="text-lg font-bold" style={{ fontFamily: "'Georgia', serif" }}>
                                Mobile Apps
                            </h3>
                        </div>

                        {/* App Store Badges */}
                        <div className="space-y-4 max-w-xs mx-auto">
                            <AppStoreBadge />
                            <GooglePlayBadge />
                        </div>

                        {/* Status indicator */}
                        <div className="pt-4">
                            <div className="inline-flex items-center gap-2 bg-stone-800/80 text-stone-400 px-4 py-2 rounded-full text-xs font-medium border border-stone-700">
                                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                În dezvoltare activă
                            </div>
                            <p className="text-xs text-stone-500 mt-3" style={{ fontFamily: "system-ui, sans-serif" }}>
                                Disponibil în curând pe iOS și Android
                            </p>
                        </div>
                    </div>

                    {/* ===== RIGHT COLUMN - CONTACT ===== */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-end gap-2 mb-6">
                            <h3 className="text-lg font-bold" style={{ fontFamily: "'Georgia', serif" }}>
                                Contact
                            </h3>
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                                <Mail className="text-white" size={16} />
                            </div>
                        </div>

                        <div className="space-y-3">
                            {contactLinks.map((link, i) => (
                                <a
                                    key={i}
                                    href={link.href}
                                    target={link.external ? '_blank' : undefined}
                                    rel={link.external ? 'noopener noreferrer' : undefined}
                                    className="group flex items-center justify-end gap-3 text-stone-400 hover:text-white transition-all duration-300 text-sm"
                                    style={{ fontFamily: "system-ui, sans-serif" }}
                                >
                                    <span className="group-hover:translate-x-1 transition-transform text-right">
                                        {link.label}
                                    </span>
                                    <span className="text-stone-500 group-hover:text-emerald-400 transition-colors">
                                        {link.icon}
                                    </span>
                                </a>
                            ))}
                        </div>

                        {/* Social Media Buttons */}
                        <div className="pt-4 flex justify-end gap-3">
                            <a
                                href="#"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-xl bg-stone-800 border border-stone-700 flex items-center justify-center text-stone-400 hover:text-white hover:bg-blue-600 hover:border-blue-500 transition-all"
                                aria-label="Facebook"
                            >
                                <Facebook size={18} />
                            </a>
                            <a
                                href="#"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-xl bg-stone-800 border border-stone-700 flex items-center justify-center text-stone-400 hover:text-white hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:border-pink-500 transition-all"
                                aria-label="Instagram"
                            >
                                <Instagram size={18} />
                            </a>
                        </div>
                    </div>
                </div>

                {/* ===== BOTTOM BAR ===== */}
                <div className="mt-16 pt-8 border-t border-stone-800">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        {/* Logo & Copyright */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center">
                                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-semibold">Affinity Club</p>
                                <p className="text-xs text-stone-500">&copy; {currentYear} Toate drepturile rezervate.</p>
                            </div>
                        </div>

                        {/* Tagline */}
                        <p className="text-sm text-stone-500 text-center" style={{ fontFamily: "system-ui, sans-serif" }}>
                            Construiește relații sănătoase cu ajutorul psihologilor noștri.
                        </p>

                        {/* Trust Badges */}
                        <div className="flex items-center gap-4 text-xs text-stone-500">
                            <span className="flex items-center gap-1.5">
                                <Shield size={12} className="text-emerald-500" />
                                GDPR
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Shield size={12} className="text-amber-500" />
                                SSL Secure
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;